<script setup lang="ts">
import { computed } from "vue";

import { getAlmInfo, getAlmType } from "@/backend/read/BackendReader";
import ComplexList from "@/components/ui/ComplexList/ComplexList.vue";
import ListBanner from "@/components/ui/ComplexList/ListBanner.vue";
import ListGroupHeader from "@/components/ui/ComplexList/ListGroupHeader.vue";
import ListHeaderContent from "@/components/ui/ComplexList/ListHeaderContent.vue";
import { useBoardStore } from "@/store/board";
import { useIssueSelectorStore } from "@/store/issueSelector";

import IssueListItem from "./IssueListItem.vue";
import IssueFilterBar from "./filters/IssueFilterBar.vue";
import EmptyConfigState from "./internal/EmptyConfigState.vue";
import EmptyFilteredState from "./internal/EmptyFilteredState.vue";
import LoadingState from "./internal/LoadingState.vue";
import SectionInfoTooltip from "./internal/SectionInfoTooltip.vue";
import { issueSelectorCardFromAlmIssue } from "./utils";

const store = useIssueSelectorStore();

const showLimitWarning = computed(() => store.totalCount >= store.limit && store.limit > 0);

/**
 * Only show results which are not already on this board
 * This can happen if the issue is in a non-iteration (or unmapped) region,
 * like "Risks". In Jira, there won't be a Sprint assigned, but the sticky is already on the board
 * This also hides the issue from the list when it's dragged onto the board
 */
const issuesNotOnBoard = computed(() => {
  const cards = useBoardStore().currentBoard().cards;

  const almIdsOnBoard = new Set();
  for (const card of Object.values(cards)) {
    if (card.data.almId) {
      almIdsOnBoard.add(card.data.almId);
    }
  }

  return store.issues
    .filter((issue) => !almIdsOnBoard.has(issue.issueId))
    .map(issueSelectorCardFromAlmIssue);
});

const almInfo = computed(() => getAlmInfo());
</script>

<template>
  <ComplexList class="issue-list">
    <template #header>
      <ListGroupHeader class="header-container">
        <ListHeaderContent
          :icon-before="almInfo.colorIcon"
          :text="$t('issueSelector.alm.subtitle', { almTool: almInfo.name })"
        />
        <template #after>
          <SectionInfoTooltip
            :description="$t('issueSelector.alm.tooltip.description', { almTool: almInfo.name })"
            :hint="getAlmType() === 'jira' ? $t('issueSelector.alm.tooltip.hint') : undefined"
            hint-icon="general/filter"
          />
        </template>
      </ListGroupHeader>

      <IssueFilterBar v-if="!store.disabled" />
    </template>
    <LoadingState v-if="store.isLoading" />
    <EmptyConfigState v-else-if="store.disabled" :message="store.disabledMessage as string" />
    <EmptyFilteredState v-else-if="store.hasActiveFilter && !issuesNotOnBoard.length" />
    <EmptyConfigState
      v-else-if="!issuesNotOnBoard.length"
      :message="$t('issueSelector.message.noIssues')"
    />

    <template v-else>
      <IssueListItem v-for="issue in issuesNotOnBoard" :key="issue.almId" :issue />

      <ListBanner v-if="showLimitWarning">
        {{ $t("issueSelector.limitReached", { count: store.limit }) }}
      </ListBanner>
    </template>
  </ComplexList>
</template>

<style lang="scss" scoped>
@use "@/styles/variables/colors";

.issue-list {
  border-top: 1px solid colors.$border-subtle;
  flex: 1;
  overflow-y: auto;

  .header-container {
    flex-grow: 0;
  }
}
</style>
